/* eslint-disable no-unused-vars */
import React, { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import {
  HeaderTitle,
  IconButton,
  LoaderWrapper,
  StyledContainer,
  TableCell,
  TableCellEmail,
  TableWrapper,
  TwoColumns,
} from '../../../services/settings/appStyle/global.style';
import { UserContainer } from './user.style';
import {
  Loader,
  Table,
  IconButton as IconButtonWithText,
  DropdownButton,
  DeleteConfirmationModal,
  // TabButton,
} from '../../../Components/uielements';
import { useUserContext } from '../../../hooks/UserContext';
import Company from '../../../services/bioplus-services/company';
import { AddPlusIcon, DeleteIcon, EditIcon } from '../../../Components/icons';
import { InviteUserModal, UserEditAndPermissionModal } from './modals';
import { ownerOption } from './constants';
import { IntlMessages } from '../../../helper/langHelper';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import SimCardDownloadIcon from '@mui/icons-material/SimCardDownload';
import { exportExcelFile } from '../../../helper/exportExcelHelper';
import moment from 'moment';
import { convertToUserTimeZoneDateTime } from '../../../helper/dataFormat';
import { sentUserInvitationsList } from '../../../services/bioplus-services/user';
// import GroupIcon from '@mui/icons-material/Group';
// import GroupAddIcon from '@mui/icons-material/GroupAdd';

const NON_DELETABLE_ROLES = ['ADMIN', 'PARTNER', 'VISITOR', 'OWNER'];
const NON_EDITABLE_ROLES = ['ADMIN', 'PARTNER', 'OWNER'];

function extractUserInfo(data) {
  const userInfo = {
    userName: `${data?.userId?.firstName} ${data?.userId?.lastName}`,
    email: data?.userId?.email,
    phone: data?.userId?.phone,
    companyName: data?.companyId?.name,
    role: data?.label,
    id: data?.userId?.id,
    _id: data?._id,
  };
  return userInfo;
}

function extractData(dataArray, userTimeZone) {
  if (dataArray) {
    const extractedData = dataArray?.map((item) => {
      return {
        Name: `${item?.userId?.firstName} ${item?.userId?.lastName}`,
        accountManager: item?.companyId?.name,
        email: item?.userId?.email,
        role: item?.label,
        mobile: item?.userId?.phone,
        createdAt: moment(item?.createdAt).format('DD-MM-YYYY'),
        lastLogin: item?.userId?.lastLogin
          ? convertToUserTimeZoneDateTime(item?.userId?.lastLogin, userTimeZone)
          : 'N/A',
      };
    });
    return { bioplusUsers: extractedData };
  }
}

// Member List Component
const UserList = ({ companyId, canManage, userEmail }) => {
  const [editUser, setEditUser] = useState();
  const [deleteUser, setDeleteUser] = useState();
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const userTimeZone = useMemo(() => Intl.DateTimeFormat().resolvedOptions().timeZone, []);

  const usersQuery = useQuery(['roles', companyId, page, pageSize], () =>
    Company.listUserRoles(companyId, page, pageSize)
  );
  const userTableData = usersQuery?.data?.data ?? [];

  const queryClient = useQueryClient();

  const deleteMutation = useMutation(
    (data) =>
      Company.removeUserFromCompany(data?.companyId?._id, data?.userId?._id, deleteUser?._id),
    {
      onSuccess: async () => {
        alert('Member deleted successfully.');
        await queryClient.invalidateQueries('roles');
      },
    }
  );

  const userColumns = useMemo(() => {
    const cols = [
      {
        Header: <IntlMessages id="table.col.name" />,
        accessor: 'userName',
        Cell: (cellData) => (
          <TableCell>
            {`${cellData?.row?.original?.userId?.firstName} ${cellData?.row?.original?.userId?.lastName}`}
          </TableCell>
        ),
        width: '15%',
      },
      {
        Header: <IntlMessages id="table.col.account.owner" />,
        accessor: 'companyName',
        width: '14%',
        Cell: (cellData) => <TableCell>{`${cellData?.row?.original?.companyId?.name}`}</TableCell>,
      },
      {
        Header: <IntlMessages id="table.col.email" />,
        accessor: 'email',
        width: '17%',
        Cell: (cellData) => (
          <TableCellEmail>{`${cellData?.row?.original?.userId?.email}`}</TableCellEmail>
        ),
      },
      {
        Header: <IntlMessages id="table.col.role" />,
        accessor: 'label',
      },
      {
        Header: <IntlMessages id="table.col.mobile.no" />,
        accessor: 'phone',
        Cell: (cellData) => <TableCell>{`${cellData?.row?.original?.userId?.phone}`}</TableCell>,
      },
      {
        Header: <IntlMessages id="table.col.createdAt" />,
        accessor: 'createdAt',
        Cell: (cellData) => (
          <TableCell>
            {cellData.value ? moment(cellData.value).format('DD-MM-YYYY') : '--'}
          </TableCell>
        ),
      },
      {
        Header: <IntlMessages id="table.col.last.login" />,
        accessor: 'lastLogin',
        width: '15%',
        Cell: (cellData) => (
          <TableCell>
            {cellData?.row?.original?.userId?.lastLogin
              ? `${convertToUserTimeZoneDateTime(
                  cellData?.row?.original?.userId?.lastLogin,
                  userTimeZone
                )}`
              : 'N/A'}
          </TableCell>
        ),
      },
    ];

    // Only show the Actions column (Edit/Delete) if the user has manage permission
    if (canManage) {
      cols.push({
        Header: <IntlMessages id="table.col.actions" defaultMessage="Actions" />,
        accessor: 'actions',
        isAction: true,
      });
    }

    return cols;
  }, [userTimeZone, canManage]);

  const handleClosed = () => {
    setEditUser(undefined);
  };

  const handleNextPage = () => {
    setPage((value) => value + 1);
  };
  const handlePreviousPage = () => {
    setPage((value) => value - 1);
  };

  const handleDeleteUser = (data) => {
    deleteMutation.mutate(data);
    setDeleteUser(undefined);
  };

  // Only expose Edit/Delete row actions if the user has manage permission
  const actionList = canManage
    ? [
        (data) => {
          const isEditDisabled =
            NON_EDITABLE_ROLES.includes(data?.role) || userEmail === data?.userId?.email;
          return (
            <IconButton
              onClick={() => !isEditDisabled && setEditUser(data)}
              disabled={isEditDisabled}
              style={isEditDisabled ? { opacity: 0.4, cursor: 'not-allowed' } : undefined}
            >
              <EditIcon />
            </IconButton>
          );
        },
        (data) => {
          const isDeleteDisabled =
            NON_DELETABLE_ROLES.includes(data?.role) || userEmail === data?.userId?.email;
          return (
            <IconButton
              onClick={() => !isDeleteDisabled && setDeleteUser(data)}
              disabled={isDeleteDisabled}
              style={isDeleteDisabled ? { opacity: 0.4, cursor: 'not-allowed' } : undefined}
            >
              <DeleteIcon style={{ width: '19px', height: '19px' }} />
            </IconButton>
          );
        },
      ]
    : [];

  const excelFormatData = extractData(usersQuery?.data?.data, userTimeZone);
  const fileName = `Members-${moment().format('DD-MM-YYYY-hh.mm A')}.xlsx`;
  const fileNameCsv = `Members-${moment().format('DD-MM-YYYY-hh.mm A')}.csv`;
  const columnConfig = [
    { header: 'Name', key: 'Name', width: 30 },
    { header: 'Account Manager', key: 'accountManager', width: 30 },
    { header: 'Email', key: 'email', width: 30 },
    { header: 'Role', key: 'role', width: 20 },
    { header: 'Mobile No', key: 'mobile', width: 20 },
    { header: 'created At', key: 'createdAt', width: 20 },
    { header: 'Last Login', key: 'lastLogin', width: 20 },
  ];

  // const handleClickItem = (item) => {
  //   switch (item?.key) {
  //     case 'excel':
  //       exportExcelFile(excelFormatData, fileName, columnConfig);
  //       break;
  //     case 'pdf':
  //       break;
  //     case 'csv':
  //       exportExcelFile(excelFormatData, fileNameCsv, columnConfig);
  //       break;
  //     default:
  //       break;
  //   }
  // };

  if (usersQuery.isLoading) {
    return (
      <LoaderWrapper>
        <Loader />
      </LoaderWrapper>
    );
  }

  return (
    <>
      <TableWrapper>
        <Table
          data={userTableData}
          columns={userColumns}
          hasFooter={true}
          actions={actionList}
          page={page}
          pageSize={pageSize}
          prevPage={handlePreviousPage}
          nextPage={handleNextPage}
          total={usersQuery?.data?.total || 0}
        />
      </TableWrapper>
      {editUser && editUser !== undefined && canManage && (
        <UserEditAndPermissionModal
          onClose={handleClosed}
          data={extractUserInfo(editUser)}
          userRole="owner"
          options={ownerOption}
          invalidateQuery="roles"
        />
      )}
      {deleteUser && canManage && (
        <DeleteConfirmationModal
          onClose={() => {
            setDeleteUser(undefined);
          }}
          message={
            <IntlMessages
              id="delete.comfirmation.text.user"
              values={{ user: `${deleteUser?.userId?.firstName} ${deleteUser?.userId?.lastName}` }}
            />
          }
          onConfirm={() => handleDeleteUser(deleteUser)}
        />
      )}
    </>
  );
};

// Sent Invitations Member List Component
const SentInvitationUserList = () => {
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const userTimeZone = useMemo(() => Intl.DateTimeFormat().resolvedOptions().timeZone, []);
  const invitationsQuery = useQuery(['sentUserInvitationsList', page, pageSize], () =>
    sentUserInvitationsList(page, pageSize)
  );
  const invitationsData = invitationsQuery?.data?.data ?? [];

  const handleNextPage = () => {
    setPage((value) => value + 1);
  };
  const handlePreviousPage = () => {
    setPage((value) => value - 1);
  };

  const invitationColumns = useMemo(
    () => [
      {
        Header: <IntlMessages id="table.col.email" />,
        accessor: 'email',
        width: '25%',
        Cell: (cellData) => <TableCellEmail>{`${cellData?.row?.original?.email}`}</TableCellEmail>,
      },
      {
        Header: <IntlMessages id="table.col.role" />,
        accessor: 'role',
        width: '25%',
      },
      {
        Header: <IntlMessages id="table.col.createdAt" />,
        accessor: 'createdAt',
        width: '25%',
        Cell: (cellData) => (
          <TableCell>
            {cellData.value ? moment(cellData.value).format('DD-MM-YYYY') : '--'}
          </TableCell>
        ),
      },

      {
        Header: <IntlMessages id="table.col.status" defaultMessage="Status" />,
        accessor: 'status',
        width: '25%',
      },
    ],
    [userTimeZone]
  );

  const actionList = [];

  if (invitationsQuery.isLoading) {
    return (
      <LoaderWrapper>
        <Loader />
      </LoaderWrapper>
    );
  }

  return (
    <TableWrapper>
      <Table
        data={invitationsData}
        columns={invitationColumns}
        actions={actionList}
        hasFooter={true}
        page={page}
        pageSize={pageSize}
        prevPage={handlePreviousPage}
        nextPage={handleNextPage}
        total={invitationsQuery?.data?.total || 0}
      />
    </TableWrapper>
  );
};

// Main Member Component
const OwnerRoles = () => {
  const userContext = useUserContext();
  const companyId = userContext?.company?.companyId?.id;
  const [isAddUserModal, setIsAddUserModal] = useState(false);
  const [selectedTab, setSelectedTab] = useState('Member List');
  const userTimeZone = useMemo(() => Intl.DateTimeFormat().resolvedOptions().timeZone, []);

  const allUsers = useQuery(['allRoles'], () => Company.listAllUsers(companyId));

  const handleClosedAddUserModal = () => {
    setIsAddUserModal(false);
  };

  // const handleSelectedTab = (selectedTab) => {
  //   setSelectedTab(selectedTab);
  // };

  const { userPermission, company } = userContext;
  const isUserAllowToManage = userPermission?.find((permission) => permission?.label === 'user');

  // Permission flags derived from the "user" permission object
  const canManageUser = !!isUserAllowToManage?.isManage;
  const canDownloadReport = !!isUserAllowToManage?.isReport;
  const canCreateUser = !!isUserAllowToManage?.isCreate;

  const excelFormatData = extractData(allUsers?.data, userTimeZone);
  const fileName = `Members-${moment().format('DD-MM-YYYY-hh.mm A')}.xlsx`;
  const fileNameCsv = `Members-${moment().format('DD-MM-YYYY-hh.mm A')}.csv`;
  const columnConfig = [
    { header: 'Name', key: 'Name', width: 30 },
    { header: 'Account Manager', key: 'accountManager', width: 30 },
    { header: 'Email', key: 'email', width: 30 },
    { header: 'Role', key: 'role', width: 20 },
    { header: 'Mobile No', key: 'mobile', width: 20 },
    { header: 'created At', key: 'createdAt', width: 20 },
    { header: 'Last Login', key: 'lastLogin', width: 20 },
  ];

  const handleClickItem = (item) => {
    // Guard: even if triggered programmatically, block export without report permission
    if (!canDownloadReport) return;

    switch (item?.key) {
      case 'excel':
        exportExcelFile(excelFormatData, fileName, columnConfig);
        break;
      case 'pdf':
        break;
      case 'csv':
        exportExcelFile(excelFormatData, fileNameCsv, columnConfig);
        break;
      default:
        break;
    }
  };

  return (
    <StyledContainer>
      <UserContainer>
        <HeaderTitle>
          <IntlMessages id="roles.module.roles" />
        </HeaderTitle>
        <TwoColumns style={{ gap: '10px' }}>
          {/* <TabButton
            buttonArray={[
              { buttonText: 'Member List', textId: '1', icon: <GroupIcon /> },
              { buttonText: 'Sent Invitations', textId: '2', icon: <GroupAddIcon /> },
            ]}
            onButtonClick={handleSelectedTab}
            selectedTab={selectedTab}
          /> */}
          <div style={{ display: 'flex', gap: '10px' }}>
            {canCreateUser && (
              <IconButtonWithText
                text={<IntlMessages id="button.text.add.user" />}
                icon={<AddPlusIcon />}
                onClick={() => {
                  setIsAddUserModal(true);
                }}
              />
            )}
            {selectedTab === 'Member List' && excelFormatData && canDownloadReport && (
              <DropdownButton
                noOfColoumns={1}
                buttonArray={[
                  { key: 'excel', title: 'Export as Excel', icon: <CloudDownloadIcon /> },
                  { key: 'csv', title: 'Export as CSV', icon: <SimCardDownloadIcon /> },
                ]}
                onClick={handleClickItem}
              />
            )}
          </div>
        </TwoColumns>
      </UserContainer>

      {selectedTab === 'Member List' ? (
        <UserList companyId={companyId} canManage={canManageUser} userEmail={company?.createdBy} />
      ) : (
        <SentInvitationUserList />
      )}

      {isAddUserModal && canCreateUser && (
        <InviteUserModal onClose={handleClosedAddUserModal} companyId={companyId} />
      )}
    </StyledContainer>
  );
};

export default OwnerRoles;
